import { approveButtonLabel } from './approvalStepCollapse';

/**
 * THE copy deck for the "approve on behalf" confirmation — the 2026-08-18
 * owner ruling from the Jake Dimmer incident (see
 * `components/ApproveOnBehalfDialog.js`'s docblock for the full story: a
 * manager holding `canManageDepartmentOrders` may approve ANY step in the
 * department, not only their own, and the app never told him so; he
 * experienced it as "I have to press approve twice"). ONE call site for the
 * wording so `DepartmentOrders.js` and `ApprovalStatus.js` cannot drift, and
 * the owner has one place to revise it — mirrors `utils/hireMoneyWarning.js`
 * for `HireMoneyWarningDialog`.
 *
 * `stepCount` is OPTIONAL, and its absence is a real case, not a stub:
 * `GET /api/orders/department` does not compute `collapsible_step_count`
 * (only `/pending` and the order-detail endpoint do — see
 * `server/helpers/approvalStepCollapse.js`), so `DepartmentOrders.js` always
 * calls these with no count and the copy reads as ONE step. Passing 1 reads
 * identically to omitting it.
 */

/**
 * The reason box's label (2026-09-14, owner: approve on behalf *"is still an
 * override"*). Same words as `utils/skipApprovalsWording.js`'s prompt, because
 * it is the same demand — each copy deck keeps its own string.
 */
export const APPROVE_ON_BEHALF_PROMPT_LABEL = 'Reason (required)';

/** @param {string|null|undefined} approverName */
export function approveOnBehalfTitle(approverName) {
  const name = approverName && approverName.trim() ? approverName : 'another approver';
  return `This step belongs to ${name}`;
}

/**
 * @param {string|null|undefined} approverName
 * @param {number|null|undefined} [stepCount] — how many sequential steps one
 *   press would decide (`utils/approvalStepCollapse.js`); omit or pass 1 for
 *   a single step.
 */
export function approveOnBehalfMessage(approverName, stepCount) {
  const name = approverName && approverName.trim() ? approverName : 'another approver';
  const count = Number(stepCount);
  const plural = Number.isFinite(count) && count > 1;
  const subject = plural ? `${count} approval steps` : 'this approval step';
  const pronoun = plural ? 'them' : 'it';
  return `${name} is the named approver for ${subject}, not you. You can approve on their behalf because you manage this department's orders. Approving now marks ${pronoun} approved and ${name} will not be asked to act — the audit trail records that you approved ${pronoun}, so it is never mistaken for ${name}'s own decision. Your reason is recorded with it, and ${name} will be told.`;
}

/**
 * The confirm button's label. Agrees with the Approve button's own
 * collapse wording (`utils/approvalStepCollapse.js#approveButtonLabel`)
 * rather than inventing a second, disagreeing phrase for the same fact —
 * when a step count is known, this IS that helper's output for a
 * not-viewer-named-approver run.
 *
 * @param {string|null|undefined} approverName
 * @param {number|null|undefined} [stepCount]
 */
export function approveOnBehalfConfirmLabel(approverName, stepCount) {
  const count = Number(stepCount);
  if (Number.isFinite(count) && count > 1) {
    return approveButtonLabel(count, false, { isViewerNamedApprover: false, approverName });
  }
  return 'Approve on their behalf';
}
